import {
  COMPANY_ADDRESS_FULL,
  COMPANY_FACTS,
  COMPANY_LOCATION,
  COMPANY_NAME,
  MAP_URL,
  RECORD_ITEMS,
} from './constants';

function Header() {
  const navItems = [
    { label: 'Profile', href: '#profile' },
    { label: 'Record', href: '#record' },
    { label: 'Location', href: '#location' },
  ];

  return (
    <header className="sticky top-0 z-30 border-b border-forest/10 bg-stone/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4">
        <a href="#top" className="flex items-center gap-3 font-semibold text-ink" aria-label={`${COMPANY_NAME} home`}>
          <span className="flex h-10 w-10 items-center justify-center rounded bg-forest text-sm font-black text-white">
            CH
          </span>
          <span className="tracking-wide">{COMPANY_NAME}</span>
        </a>
        <nav className="hidden items-center gap-7 text-sm font-medium text-mute md:flex" aria-label="Primary">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} className="transition hover:text-forest">
              {item.label}
            </a>
          ))}
        </nav>
        <a
          href="#location"
          className="rounded bg-copper px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#92400e]"
        >
          Address
        </a>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="profile" className="border-b border-forest/10 bg-stone">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 md:grid-cols-[1.05fr_0.95fr] md:items-center lg:py-24">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-forest">
            Lancaster Business Profile
          </p>
          <h1 className="mt-5 max-w-4xl text-5xl font-black tracking-normal text-ink sm:text-6xl lg:text-7xl">
            {COMPANY_NAME}
          </h1>
          <p className="mt-6 max-w-2xl text-xl leading-8 text-mute">
            A concise public business profile for a Lancaster, Ohio organization.
          </p>
          <p className="mt-5 max-w-2xl leading-7 text-slate-600">
            The page is intentionally limited to supplied public-facing details and avoids adding unverified contact,
            service, or registration information.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a
              href="#record"
              className="rounded bg-forest px-5 py-3 text-center font-semibold text-white shadow-sm transition hover:bg-[#0f3f22]"
            >
              View Record
            </a>
            <a
              href="#location"
              className="rounded border border-forest/25 px-5 py-3 text-center font-semibold text-forest transition hover:border-copper hover:text-copper"
            >
              Mailing Location
            </a>
          </div>
        </div>

        <aside className="rounded border border-forest/10 bg-surface p-6 shadow-soft" aria-label="Company profile summary">
          <div className="border-b border-stone pb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-copper">Profile Panel</p>
            <p className="mt-5 text-3xl font-black text-ink">{COMPANY_NAME}</p>
          </div>
          <dl className="divide-y divide-stone">
            <div className="grid gap-2 py-5 sm:grid-cols-[0.42fr_0.58fr]">
              <dt className="text-sm font-semibold uppercase tracking-wide text-mute">Location</dt>
              <dd className="font-bold text-ink">{COMPANY_LOCATION}</dd>
            </div>
            <div className="grid gap-2 py-5 sm:grid-cols-[0.42fr_0.58fr]">
              <dt className="text-sm font-semibold uppercase tracking-wide text-mute">Address</dt>
              <dd className="font-bold text-ink">{COMPANY_ADDRESS_FULL}</dd>
            </div>
            <div className="grid gap-2 py-5 sm:grid-cols-[0.42fr_0.58fr]">
              <dt className="text-sm font-semibold uppercase tracking-wide text-mute">Use</dt>
              <dd className="font-bold text-ink">Public directory reference</dd>
            </div>
          </dl>
        </aside>
      </div>
    </section>
  );
}

function FactBand() {
  return (
    <section aria-label="Company facts" className="bg-forest text-white">
      <div className="mx-auto grid max-w-6xl divide-y divide-white/15 px-5 md:grid-cols-3 md:divide-x md:divide-y-0">
        {COMPANY_FACTS.map((fact) => (
          <div key={fact.label} className="py-7 md:px-6 first:md:pl-0 last:md:pr-0">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/65">{fact.label}</p>
            <p className="mt-3 text-2xl font-black">{fact.value}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function RecordSection() {
  return (
    <section id="record" className="bg-surface">
      <div className="mx-auto max-w-6xl px-5 py-16 lg:py-20">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-copper">Record Overview</p>
          <h2 className="mt-3 text-3xl font-black text-ink sm:text-4xl">Source-limited company information.</h2>
          <p className="mt-4 leading-7 text-mute">
            This section uses a record-style layout to keep the page factual, compact, and distinct from the referenced
            card-heavy examples.
          </p>
        </div>

        <div className="mt-12 border-l-2 border-forest/20">
          {RECORD_ITEMS.map((item, index) => (
            <article key={item.title} className="relative pl-7 pb-10 last:pb-0">
              <span className="absolute -left-[9px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-copper ring-4 ring-white" />
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-forest">
                {String(index + 1).padStart(2, '0')} / {item.label}
              </p>
              <h3 className="mt-3 text-2xl font-bold text-ink">{item.title}</h3>
              <p className="mt-3 max-w-3xl leading-7 text-mute">{item.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Location() {
  return (
    <section id="location" className="bg-stone">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 md:grid-cols-[0.85fr_1.15fr] md:items-center lg:py-20">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-forest">Mailing Location</p>
          <h2 className="mt-3 text-3xl font-black text-ink sm:text-4xl">{COMPANY_LOCATION}</h2>
          <p className="mt-4 leading-7 text-mute">
            Use the supplied mailing address below for public profile and directory reference. No additional contact
            details are shown because they were not included in the source file.
          </p>
        </div>
        <div className="rounded border border-forest/10 bg-surface p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-copper">Address</p>
          <p className="mt-3 text-2xl font-black text-ink">{COMPANY_ADDRESS_FULL}</p>
          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-6 inline-flex rounded bg-forest px-5 py-3 font-semibold text-white transition hover:bg-[#0f3f22]"
          >
            Open map
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-ink text-white/75">
      <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-8 text-sm md:flex-row md:items-center md:justify-between">
        <p className="font-semibold text-white">{COMPANY_NAME}</p>
        <p>{COMPANY_LOCATION}</p>
      </div>
    </footer>
  );
}

function App() {
  return (
    <div id="top" className="min-h-screen bg-stone text-ink">
      <a
        href="#profile"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-forest focus:px-3 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <Header />
      <main>
        <Hero />
        <FactBand />
        <RecordSection />
        <Location />
      </main>
      <Footer />
    </div>
  );
}

export default App;
